import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useUsers, UserWithRole } from '@/hooks/useUsers';
import { useRoleAccess } from '@/hooks/useRoleAccess';

interface EditUserDialogProps {
  user: UserWithRole | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EditUserDialog = ({ user, open, onOpenChange }: EditUserDialogProps) => {
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    role: user?.role || 'staff',
  });

  const { updateUserProfile, updateUserRole, isUpdatingProfile, isUpdatingRole } = useUsers();
  const { userRole } = useRoleAccess();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !formData.name || !formData.email || !formData.role) {
      return;
    }

    // Update profile
    updateUserProfile({
      userId: user.id,
      name: formData.name,
      email: formData.email,
    });

    // Update role if changed
    if (formData.role !== user.role && user.tenant_id) {
      updateUserRole({
        userId: user.id,
        role: formData.role,
        tenantId: user.tenant_id,
        unitId: user.unit_id,
      });
    }

    onOpenChange(false);
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Update form data when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        role: user.role || 'staff',
      });
    }
  }, [user]);

  // Only show if user has permission to edit users (owner, admin, or manager)
  if (!['owner', 'admin', 'manager'].includes(userRole)) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-panel border-0">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Enter user's full name"
              className="glass-input"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="Enter user's email"
              className="glass-input"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="role">Role</Label>
            <Select value={formData.role} onValueChange={(value) => handleChange('role', value)}>
              <SelectTrigger className="glass-input">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="glass-panel border-0">
                <SelectItem value="viewer">Viewer</SelectItem>
                <SelectItem value="staff">Staff Member</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                {userRole === 'owner' && (
                  <>
                    <SelectItem value="admin">Administrator</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex justify-end gap-2 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              className="glass-button"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isUpdatingProfile || isUpdatingRole}
              className="cta-primary"
            >
              {isUpdatingProfile || isUpdatingRole ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};